import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { useConversationList } from '../../../hooks/useConversationList';
import { useTicketList } from '../../../hooks/useTicketList';
import { useConversationMessages } from '../../../hooks/useConversationMessages';
import { useRealtimeSubscription } from '../../../hooks/useRealtimeSubscription';
import ChatService from '../../../services/chatService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import TicketDetail from '../../../components/admin/TicketDetail';
import EscalateToTicketModal from '../../../components/admin/EscalateToTicketModal';

const AdminSupportDashboard = () => {
  const { userProfile } = useAuth();
  
  // Validate admin role for security
  useEffect(() => {
    if (userProfile && userProfile.role !== 'admin') {
      console.warn('Non-admin user attempted to access admin support dashboard');
    }
  }, [userProfile]);

  // State management via custom hooks
  const conversationList = useConversationList();
  const ticketList = useTicketList();
  
  // Local UI state
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [activeTab, setActiveTab] = useState('conversations');
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketDetail, setShowTicketDetail] = useState(false);
  const [showEscalateModal, setShowEscalateModal] = useState(false);
  const [uiError, setUiError] = useState(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({ show: false, conversation: null, isBulk: false, conversations: [] });
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Stable callback for messages loaded
  const handleMessagesLoaded = useCallback((count) => {
    console.log('Messages loaded:', count);
  }, []);
  
  // Conversation messages hook
  const {
    messages,
    loading: messagesLoading,
    error: messagesError,
    isAtBottom,
    loadMessages,
    sendMessage,
    handleScroll: handleMessageScroll
  } = useConversationMessages(selectedConversation?.id, handleMessagesLoaded);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Memoized color mappings
  const statusColors = useMemo(() => ({
    open: 'text-red-600 bg-red-50 border-red-200',
    in_progress: 'text-blue-600 bg-blue-50 border-blue-200',
    resolved: 'text-green-600 bg-green-50 border-green-200',
    closed: 'text-gray-600 bg-gray-50 border-gray-200'
  }), []);

  const priorityColors = useMemo(() => ({
    low: 'text-gray-600 bg-gray-50',
    normal: 'text-blue-600 bg-blue-50',
    high: 'text-orange-600 bg-orange-50',
    urgent: 'text-red-600 bg-red-50'
  }), []);

  // Scroll to bottom of messages
  const scrollToBottom = useCallback((immediate = false) => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: immediate ? 'auto' : 'smooth',
        block: 'end'
      });
    }
  }, []);

  // Track scroll position
  const trackScroll = useCallback(() => {
    if (messagesContainerRef.current) {
      handleMessageScroll(messagesContainerRef.current);
    }
  }, [handleMessageScroll]);

  // Auto-scroll when messages arrive
  useEffect(() => {
    if (messagesContainerRef.current && messages.length > 0) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      const isAtBottom = scrollHeight <= clientHeight || distanceFromBottom < 100;
      
      if (isAtBottom) {
        setTimeout(() => scrollToBottom(), 100);
      }
    }
  }, [messages, scrollToBottom]);

  // Immediate scroll when selecting conversation
  useEffect(() => {
    if (selectedConversation && messages.length > 0) {
      setTimeout(() => scrollToBottom(true), 150);
    }
  }, [selectedConversation?.id, scrollToBottom, messages.length]);

  // Show scroll button when not at bottom
  useEffect(() => {
    setShowScrollButton(!isAtBottom && messages.length > 0);
  }, [isAtBottom, messages.length]);

  // Load initial data
  useEffect(() => {
    if (!userProfile) {
      console.log('[AdminSupportDashboard] Skipping data load - no userProfile');
      return;
    }

    console.log('[AdminSupportDashboard] Loading initial data for user:', userProfile.id);
    conversationList.loadConversations();
    ticketList.loadTickets();
    ticketList.loadAnalytics();
  }, [userProfile]); // intentional: load data on mount and user change

  // Load messages when conversation changes
  useEffect(() => {
    if (selectedConversation?.id) {
      loadMessages();
    }
  }, [selectedConversation?.id, loadMessages]);

  // Send message as admin
  const handleSendMessage = useCallback(async () => {
    if (!newMessage.trim() || !selectedConversation || !userProfile) {
      setUiError('Missing required fields');
      return;
    }

    const messageContent = newMessage.trim();
    setNewMessage('');

    const result = await sendMessage(messageContent, userProfile.id, true);

    if (result.success) {
      // Auto-update conversation status if needed
      if (selectedConversation.status === 'open' || !selectedConversation.admin_id) {
        const newStatus = selectedConversation.status === 'open' ? 'in_progress' : selectedConversation.status;
        await conversationList.updateConversationStatus(
          selectedConversation.id,
          newStatus,
          userProfile.id
        );
        setSelectedConversation(prev => ({ ...prev, status: newStatus }));
      }
    } else {
      setUiError(result.error || 'Failed to send message');
      // Restore message on failure
      setNewMessage(messageContent);
    }
  }, [newMessage, selectedConversation, userProfile, sendMessage, conversationList]);

  // Update conversation status
  const handleStatusChange = useCallback(async (conversationId, newStatus) => {
    if (!userProfile) {
      setUiError('User profile is required');
      return;
    }

    const result = await conversationList.updateConversationStatus(
      conversationId,
      newStatus,
      userProfile.id
    );

    if (!result.success) {
      setUiError('Failed to update conversation status');
    }

    if (selectedConversation?.id === conversationId) {
      setSelectedConversation(prev => ({ ...prev, status: newStatus }));
    }
  }, [userProfile, selectedConversation, conversationList]);

  // Update conversation priority
  const handlePriorityChange = useCallback(async (conversationId, newPriority) => {
    if (!userProfile) {
      setUiError('User profile is required');
      return;
    }

    const result = await ChatService.updateConversationPriority(conversationId, newPriority);

    if (!result.success) {
      setUiError(`Failed to update conversation priority: ${result.error}`);
      return;
    }

    if (selectedConversation?.id === conversationId) {
      setSelectedConversation(prev => ({ ...prev, priority: newPriority }));
    }
  }, [userProfile, selectedConversation]);

  // Bulk update status
  const handleBulkStatusUpdate = useCallback(async (newStatus) => {
    if (!userProfile) {
      setUiError('User profile is required');
      return;
    }

    const convIds = Array.from(conversationList.selectedConversations);
    const result = await conversationList.bulkUpdateStatus(convIds, newStatus, userProfile.id);
    
    if (!result.success) {
      setUiError('Failed to update some conversations');
    }
  }, [userProfile, conversationList]);

  // Handle delete conversation
  const handleDeleteConversation = useCallback(async (conversation) => {
    setDeleteConfirmModal({ show: true, conversation, isBulk: false });
  }, []);

  // Handle bulk delete archived conversations
  const handleBulkDelete = useCallback(() => {
    // Only allow deleting archived conversations for safety
    const selectedIds = Array.from(conversationList.selectedConversations);
    const archivedConversations = conversationList.allFilteredConversations
      .filter(conv => selectedIds.includes(conv.id) && conv.is_archived === true);
    
    if (archivedConversations.length === 0) {
      setUiError('No archived conversations selected. Only archived conversations can be deleted.');
      return;
    }

    setDeleteConfirmModal({ 
      show: true, 
      conversation: null, 
      isBulk: true,
      conversations: archivedConversations 
    });
  }, [conversationList]);

  // Confirm delete conversation(s)
  const confirmDelete = useCallback(async () => {
    const { conversation, isBulk, conversations } = deleteConfirmModal;
    
    console.log('[AdminSupportDashboard] confirmDelete called:', { isBulk, conversation, conversations });
    
    setIsDeleting(true);
    try {
      if (isBulk && conversations) {
        // Bulk delete
        let successCount = 0;
        let failCount = 0;

        for (const conv of conversations) {
          console.log('[AdminSupportDashboard] Deleting conversation:', conv.id, 'is_archived:', conv.is_archived);
          const result = await ChatService.deleteConversation(conv.id);
          console.log('[AdminSupportDashboard] Delete result:', result);
          
          if (result.success) {
            successCount++;
            // Clear selection if deleted conversation was selected
            if (selectedConversation?.id === conv.id) {
              setSelectedConversation(null);
            }
          } else {
            failCount++;
            console.error(`Failed to delete conversation ${conv.id}:`, result.error);
          }
        }

        console.log('[AdminSupportDashboard] Bulk delete complete:', { successCount, failCount });

        // Refresh the list
        await conversationList.loadConversations();
        // Clear selections
        conversationList.setSelectedConversations(new Set());
        
        setDeleteConfirmModal({ show: false, conversation: null, isBulk: false });
        
        if (failCount > 0) {
          setUiError(`Deleted ${successCount} conversation(s). Failed to delete ${failCount}.`);
        }
      } else if (conversation) {
        // Single delete
        console.log('[AdminSupportDashboard] Deleting single conversation:', conversation.id, 'is_archived:', conversation.is_archived);
        const result = await ChatService.deleteConversation(conversation.id);
        console.log('[AdminSupportDashboard] Single delete result:', result);

        if (result.success) {
          // Remove from the list by calling load again
          await conversationList.loadConversations();
          // Clear selection if deleted conversation was selected
          if (selectedConversation?.id === conversation.id) {
            setSelectedConversation(null);
          }
          setDeleteConfirmModal({ show: false, conversation: null, isBulk: false });
        } else {
          console.error('[AdminSupportDashboard] Delete failed:', result.error);
          setUiError(`Failed to delete conversation: ${result.error}`);
        }
      }
    } catch (error) {
      console.error('[AdminSupportDashboard] Error deleting conversation:', error);
      setUiError('An error occurred while deleting the conversation');
    } finally {
      setIsDeleting(false);
    }
  }, [deleteConfirmModal, conversationList, selectedConversation?.id]);

  // Filter and sort conversations (now handled by hook with memoization)
  const filteredConversations = conversationList.filteredConversations;

  // Load tickets (now handled by hook)
  const filteredTickets = ticketList.filteredTickets;

  // Bulk update ticket status
  const handleBulkTicketStatusUpdate = useCallback(async (newStatus) => {
    if (!userProfile) {
      setUiError('User profile is required');
      return;
    }

    const ticketIds = Array.from(ticketList.selectedTickets);
    const result = await ticketList.bulkUpdateStatus(ticketIds, newStatus);
    
    if (!result.success) {
      setUiError('Failed to update some tickets');
    } else {
      await ticketList.loadAnalytics();
    }
  }, [userProfile, ticketList]);

  // Bulk assign tickets (optional feature)
  const handleBulkTicketAssign = useCallback(async (adminId) => {
    if (!userProfile) {
      setUiError('User profile is required');
      return;
    }

    try {
      setUiError(null);
      const result = await ChatService.bulkAssignTickets(Array.from(ticketList.selectedTickets), adminId);
      if (result.success) {
        ticketList.setSelectedTickets(new Set());
        await ticketList.loadTickets();
      } else {
        setUiError('Failed to assign some tickets');
      }
    } catch (error) {
      console.error('Failed to bulk assign tickets:', error);
      setUiError(error.message || 'Failed to assign tickets');
    }
  }, [userProfile, ticketList]);

  // Handle ticket detail update
  const handleTicketUpdate = useCallback((updatedTicket) => {
    ticketList.updateTicket(updatedTicket.id, updatedTicket);
    ticketList.loadAnalytics();
    // Refresh the tickets list to ensure proper data sync
    ticketList.loadTickets();
  }, [ticketList]);

  // Real-time subscription for new messages
  const handleMessageUpdate = useCallback(async () => {
    if (selectedConversation?.id) {
      console.log('[AdminSupportDashboard] Real-time message received, reloading messages...');
      await loadMessages();
    }
  }, [selectedConversation?.id, loadMessages]);

  useRealtimeSubscription(
    selectedConversation ? `admin-messages:${selectedConversation.id}` : null,
    selectedConversation ? [{
      event: 'INSERT',
      schema: 'public',
      table: 'chat_messages',
      filter: `conversation_id=eq.${selectedConversation.id}`
    }] : [],
    handleMessageUpdate
  );

  // Real-time subscription for conversation updates
  const handleConversationUpdate = useCallback(async () => {
    await conversationList.loadConversations();
  }, []); // intentional: stable callback reference

  useRealtimeSubscription(
    activeTab === 'conversations' ? 'admin-conversations' : null,
    activeTab === 'conversations' ? [{
      event: '*',
      schema: 'public',
      table: 'chat_conversations'
    }] : [],
    handleConversationUpdate
  );

  // Real-time subscription for ticket updates
  const handleTicketRealtimeUpdate = useCallback(async () => {
    await ticketList.loadTickets();
    await ticketList.loadAnalytics();
  }, []); // intentional: stable callback reference

  useRealtimeSubscription(
    activeTab === 'tickets' ? 'admin-tickets' : null,
    activeTab === 'tickets' ? [{
      event: '*',
      schema: 'public',
      table: 'support_tickets'
    }] : [],
    handleTicketRealtimeUpdate
  );

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-8 pb-20 md:pb-8">
        <div>
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-text-primary">Customer Support Hub</h1>
                <p className="text-text-secondary mt-1">Manage customer conversations and support tickets</p>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-border">
              {[
                { id: 'conversations', label: 'Conversations', icon: 'MessageCircle' },
                { id: 'tickets', label: 'Support Tickets', icon: 'Ticket' },
                { id: 'analytics', label: 'Analytics', icon: 'TrendingUp' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-3 flex items-center gap-2 border-b-2 font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-text-secondary hover:text-text-primary'
                  }`}
                >
                  <Icon name={tab.icon} size={18} />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <>
              {/* Conversations Analytics */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-text-primary mb-4">Conversations Analytics</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-white rounded-lg border border-border p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-text-muted text-sm font-medium">Total Conversations</p>
                        <p className="text-3xl font-bold text-text-primary mt-2">
                          {conversationList.conversations?.length || 0}
                        </p>
                      </div>
                      <Icon name="MessageCircle" size={40} className="text-blue-500 opacity-20" />
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-border p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-text-muted text-sm font-medium">Resolution Rate</p>
                        <p className="text-3xl font-bold text-text-primary mt-2">
                          {conversationList.loading ? '-' : '0'}%
                        </p>
                      </div>
                      <Icon name="CheckCircle" size={40} className="text-green-500 opacity-20" />
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-border p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-text-muted text-sm font-medium">Avg Resolution Time</p>
                        <p className="text-3xl font-bold text-text-primary mt-2">
                          {conversationList.loading ? '-' : '0'}h
                        </p>
                      </div>
                      <Icon name="Clock" size={40} className="text-orange-500 opacity-20" />
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-border p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-text-muted text-sm font-medium">Customer Satisfaction</p>
                        <div className="flex items-center gap-1 mt-2">
                          <p className="text-3xl font-bold text-text-primary">
                            {conversationList.loading ? '-' : '0'}
                          </p>
                          <Icon name="Star" size={24} className="text-yellow-500" fill="currentColor" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Detailed Stats */}
                  <div className="col-span-1 md:col-span-2 lg:col-span-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-white rounded-lg border border-border p-4">
                        <p className="text-text-muted text-sm">Open</p>
                        <p className="text-2xl font-bold text-red-600 mt-1">
                          {conversationList.conversations?.filter(c => c.status === 'open')?.length || 0}
                        </p>
                      </div>
                      <div className="bg-white rounded-lg border border-border p-4">
                        <p className="text-text-muted text-sm">In Progress</p>
                        <p className="text-2xl font-bold text-blue-600 mt-1">
                          {conversationList.conversations?.filter(c => c.status === 'in_progress')?.length || 0}
                        </p>
                      </div>
                      <div className="bg-white rounded-lg border border-border p-4">
                        <p className="text-text-muted text-sm">Resolved</p>
                        <p className="text-2xl font-bold text-green-600 mt-1">
                          {conversationList.conversations?.filter(c => c.status === 'resolved')?.length || 0}
                        </p>
                      </div>
                      <div className="bg-white rounded-lg border border-border p-4">
                        <p className="text-text-muted text-sm">High Priority</p>
                        <p className="text-2xl font-bold text-orange-600 mt-1">
                          {conversationList.conversations?.filter(c => ['urgent', 'high'].includes(c.priority))?.length || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tickets Analytics */}
              <div>
                <h3 className="text-xl font-semibold text-text-primary mb-4">Tickets Analytics</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-white rounded-lg border border-border p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-text-muted text-sm font-medium">Total Tickets</p>
                        <p className="text-3xl font-bold text-text-primary mt-2">
                          {ticketList.tickets?.length || 0}
                        </p>
                      </div>
                      <Icon name="Ticket" size={40} className="text-purple-500 opacity-20" />
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-border p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-text-muted text-sm font-medium">Ticket Resolution Rate</p>
                        <p className="text-3xl font-bold text-text-primary mt-2">
                          {ticketList.loading ? '-' : '0'}%
                        </p>
                      </div>
                      <Icon name="CheckCircle" size={40} className="text-green-500 opacity-20" />
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-border p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-text-muted text-sm font-medium">Avg Ticket Resolution</p>
                        <p className="text-3xl font-bold text-text-primary mt-2">
                          {ticketList.loading ? '-' : '0'}h
                        </p>
                      </div>
                      <Icon name="Clock" size={40} className="text-orange-500 opacity-20" />
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-border p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-text-muted text-sm font-medium">Urgent Tickets</p>
                        <p className="text-3xl font-bold text-text-primary mt-2">
                          {ticketList.tickets?.filter(t => t.priority === 'urgent')?.length || 0}
                        </p>
                      </div>
                      <Icon name="AlertTriangle" size={40} className="text-red-500 opacity-20" />
                    </div>
                  </div>

                  {/* Ticket Status Breakdown */}
                  <div className="col-span-1 md:col-span-2 lg:col-span-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-white rounded-lg border border-border p-4">
                        <p className="text-text-muted text-sm">Open Tickets</p>
                        <p className="text-2xl font-bold text-red-600 mt-1">
                          {ticketList.tickets?.filter(t => t.status === 'open')?.length || 0}
                        </p>
                      </div>
                      <div className="bg-white rounded-lg border border-border p-4">
                        <p className="text-text-muted text-sm">In Progress</p>
                        <p className="text-2xl font-bold text-blue-600 mt-1">
                          {ticketList.tickets?.filter(t => t.status === 'in_progress')?.length || 0}
                        </p>
                      </div>
                      <div className="bg-white rounded-lg border border-border p-4">
                        <p className="text-text-muted text-sm">Resolved</p>
                        <p className="text-2xl font-bold text-green-600 mt-1">
                          {ticketList.tickets?.filter(t => t.status === 'resolved')?.length || 0}
                        </p>
                      </div>
                      <div className="bg-white rounded-lg border border-border p-4">
                        <p className="text-text-muted text-sm">Closed</p>
                        <p className="text-2xl font-bold text-gray-600 mt-1">
                          {ticketList.tickets?.filter(t => t.status === 'closed')?.length || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Conversations Tab */}
          {activeTab === 'conversations' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Conversations List */}
              <div className="lg:col-span-1 bg-white rounded-lg border border-border overflow-hidden flex flex-col h-[calc(100vh-12rem)]">
                <div className="p-4 border-b border-border sticky top-0 bg-white z-10">
                  <h2 className="font-semibold text-text-primary mb-4">Conversations</h2>

                  {/* Search */}
                  <div className="relative mb-4">
                    <Icon name="Search" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={conversationList.searchQuery}
                      onChange={(e) => conversationList.setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      aria-label="Search conversations"
                    />
                  </div>

                  {/* Filters */}
                  <div className="flex gap-2 mb-4 overflow-x-auto">
                    {['all', 'open', 'in_progress', 'resolved'].map(status => (
                      <button
                        key={status}
                    onClick={() => {
                      conversationList.setFilter(status);
                      conversationList.setCurrentPage(1);
                    }}
                    className={`px-3 py-1 text-xs font-medium rounded-lg whitespace-nowrap transition-colors ${
                      conversationList.filter === status
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    aria-pressed={conversationList.filter === status}
                  >
                    {status === 'all' ? 'All' : status.replace('_', ' ')}
                  </button>
                ))}
              </div>

              {/* Show Archived Toggle */}
              <div className="flex items-center gap-2 mb-4 pb-4 border-b border-gray-200">
                <input
                  type="checkbox"
                  id="show-archived"
                  checked={conversationList.showArchived}
                  onChange={(e) => {
                    conversationList.setShowArchived(e.target.checked);
                    conversationList.setCurrentPage(1);
                  }}
                  className="w-4 h-4 rounded"
                />
                <label htmlFor="show-archived" className="text-xs font-medium text-text-muted cursor-pointer">
                  Show Archived ({conversationList.conversations?.filter(c => c.is_archived === true).length || 0})
                </label>
              </div>

              {/* Sort */}
              <select
                value={conversationList.sortBy}
                onChange={(e) => conversationList.setSortBy(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                aria-label="Sort conversations"
              >
                <option value="updated">Sort by Updated</option>
                <option value="created">Sort by Created</option>
                <option value="priority">Sort by Priority</option>
              </select>

              {/* Bulk Actions */}
              {conversationList.selectedConversations.size > 0 && (
                <div className="mt-4 pt-4 border-t border-border">
                      <p className="text-xs text-text-muted mb-2">
                        {conversationList.selectedConversations.size} selected
                      </p>
                      <div className="flex gap-2 flex-col">
                        <select
                          onChange={(e) => {
                            if (e.target.value) {
                              handleBulkStatusUpdate(e.target.value);
                              e.target.value = '';
                            }
                          }}
                          className="w-full px-2 py-1 border border-border rounded text-xs"
                          defaultValue=""
                          aria-label="Bulk update conversation status"
                        >
                          <option value="">Update status...</option>
                          <option value="open">Mark Open</option>
                          <option value="in_progress">Mark In Progress</option>
                          <option value="resolved">Mark Resolved</option>
                          <option value="closed">Mark Closed</option>
                        </select>
                        <button
                          onClick={() => {
                            const convIds = Array.from(conversationList.selectedConversations);
                            conversationList.bulkArchiveConversations(convIds, userProfile.id);
                          }}
                          className="w-full px-2 py-1 bg-orange-100 hover:bg-orange-200 text-orange-700 rounded text-xs font-medium transition-colors"
                        >
                          Archive Selected
                        </button>
                        {conversationList.showArchived && (
                          <button
                            onClick={handleBulkDelete}
                            className="w-full px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded text-xs font-medium transition-colors"
                          >
                            Delete Selected
                          </button>
                        )}
                        <button
                          onClick={() => conversationList.setSelectedConversations(new Set())}
                          className="px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex-1 overflow-y-auto">
                  {(() => {
                    console.log('[AdminSupportDashboard] Rendering conversation list:', {
                      loading: conversationList.loading,
                      error: conversationList.error,
                      allFilteredCount: conversationList.allFilteredConversations.length,
                      paginatedCount: conversationList.filteredConversations.length,
                      totalConversations: conversationList.conversations.length,
                      showArchived: conversationList.showArchived,
                      currentPage: conversationList.currentPage,
                      totalPages: conversationList.totalPages
                    });
                    
                    if (conversationList.loading) {
                      return (
                        <div className="p-4 text-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                        </div>
                      );
                    }
                    
                    if (conversationList.error) {
                      return (
                        <div className="p-4 text-center text-red-600 text-sm">
                          {conversationList.error}
                        </div>
                      );
                    }
                    
                    if (conversationList.allFilteredConversations.length === 0) {
                      return (
                        <div className="p-4 text-center text-text-muted">
                          No conversations found
                        </div>
                      );
                    }
                    
                    return conversationList.filteredConversations.map(conversation => (
                      <div
                        key={conversation.id}
                        className={`p-4 border-b border-border cursor-pointer hover:bg-gray-50 transition-colors ${
                          selectedConversation?.id === conversation.id ? 'bg-primary-50' : ''
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          <input
                            type="checkbox"
                            checked={conversationList.selectedConversations.has(conversation.id)}
                            onChange={(e) => {
                              const newSet = new Set(conversationList.selectedConversations);
                              if (e.target.checked) {
                                newSet.add(conversation.id);
                              } else {
                                newSet.delete(conversation.id);
                              }
                              conversationList.setSelectedConversations(newSet);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="mt-1"
                            aria-label={`Select conversation from ${conversation.user?.full_name}`}
                          />
                          <button
                            onClick={() => setSelectedConversation(conversation)}
                            className="flex-1 text-left"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <h3 className="font-medium text-text-primary text-sm truncate">
                                  {conversation.user?.full_name}
                                  {conversation.is_archived === true && (
                                    <span className="ml-2 text-xs text-gray-500 font-normal">(Archived)</span>
                                  )}
                                </h3>
                              </div>
                              <span className={`px-2 py-1 text-xs font-medium rounded border whitespace-nowrap ${statusColors[conversation.status]}`}>
                                {conversation.status}
                              </span>
                            </div>
                            <p className="text-xs text-text-muted mb-2 truncate">{conversation.subject}</p>
                            <div className="flex items-center justify-between gap-2">
                              <span className={`text-xs px-2 py-0.5 rounded ${priorityColors[conversation.priority]}`}>
                                {conversation.priority}
                              </span>
                              <span className="text-xs text-text-muted">
                                {new Date(conversation.updated_at).toLocaleDateString()}
                              </span>
                            </div>
                          </button>
                          <div className="flex gap-1 flex-shrink-0">
                            {conversation.is_archived !== true && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  conversationList.archiveConversation(conversation.id, userProfile.id);
                                }}
                                className="p-1 hover:bg-red-100 rounded text-red-600 transition-colors"
                                title="Archive conversation"
                                aria-label={`Archive conversation with ${conversation.user?.full_name}`}
                              >
                                <Icon name="Archive" size={16} />
                              </button>
                            )}
                            {conversation.is_archived === true && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteConversation(conversation);
                                }}
                                className="p-1 hover:bg-red-100 rounded text-red-600 transition-colors"
                                title="Delete archived conversation"
                                aria-label={`Delete archived conversation with ${conversation.user?.full_name}`}
                              >
                                <Icon name="Trash2" size={16} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
                {/* Pagination Controls */}
              {conversationList.totalPages > 1 && (
                <div className="p-4 border-t border-border flex items-center justify-between bg-gray-50">
                  <button
                    onClick={() => conversationList.setCurrentPage(Math.max(1, conversationList.currentPage - 1))}
                    disabled={conversationList.currentPage === 1}
                    className="px-3 py-1 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition-colors"
                  >
                    ← Previous
                  </button>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-text-muted">
                      Page {conversationList.currentPage} of {conversationList.totalPages}
                    </span>
                  </div>
                  <button
                    onClick={() => conversationList.setCurrentPage(Math.min(conversationList.totalPages, conversationList.currentPage + 1))}
                    disabled={conversationList.currentPage === conversationList.totalPages}
                    className="px-3 py-1 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition-colors"
                  >
                    Next →
                  </button>
                </div>
              )}
              </div>

              {/* Chat Panel */}
              <div className="lg:col-span-2 bg-white rounded-lg border border-border flex flex-col h-[calc(100vh-12rem)] relative">
                {selectedConversation ? (
                  <>
                    {/* Header */}
                    <div className="p-4 border-b border-border flex items-center justify-between">
                      <div>
                        <h2 className="font-semibold text-text-primary">
                          {selectedConversation.user?.full_name}
                        </h2>
                        <p className="text-sm text-text-muted">{selectedConversation.subject}</p>
                        <p className="text-xs text-text-muted mt-1">
                          {selectedConversation.user?.email}
                        </p>
                      </div>
                      <div className="flex flex-col gap-2">
                        <select
                          value={selectedConversation.status}
                          onChange={(e) => handleStatusChange(selectedConversation.id, e.target.value)}
                          className="px-3 py-2 border border-border rounded-lg text-sm"
                        >
                          <option value="open">Open</option>
                          <option value="in_progress">In Progress</option>
                          <option value="resolved">Resolved</option>
                          <option value="closed">Closed</option>
                        </select>
                        <select
                          value={selectedConversation.priority}
                          onChange={(e) => handlePriorityChange(selectedConversation.id, e.target.value)}
                          className="px-3 py-2 border border-border rounded-lg text-sm"
                        >
                          <option value="low">Low</option>
                          <option value="normal">Normal</option>
                          <option value="high">High</option>
                          <option value="urgent">Urgent</option>
                        </select>
                      </div>
                    </div>

                    {/* Messages */}
                    <div 
                      ref={messagesContainerRef}
                      onScroll={trackScroll}
                      className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 relative"
                      role="log"
                      aria-label="Conversation messages"
                    >
                      {messagesError && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                          {messagesError}
                        </div>
                      )}
                      
                      {messagesLoading && messages.length === 0 && (
                        <div className="flex items-center justify-center h-full">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        </div>
                      )}

                      {messages.length === 0 && !messagesLoading && (
                        <div className="flex items-center justify-center h-full text-gray-400">
                          <p>No messages yet</p>
                        </div>
                      )}

                      {messages.map(message => (
                        <div
                          key={message.id}
                          className={`flex ${message.is_admin ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-xs px-4 py-2 rounded-lg ${
                              message.is_admin
                                ? 'bg-primary text-white'
                                : 'bg-white text-text-primary border border-border'
                            }`}
                          >
                            <p className="text-sm">{message.message_text}</p>
                            <p className={`text-xs mt-1 ${message.is_admin ? 'text-primary-100' : 'text-text-muted'}`}>
                              {message.sender?.full_name} • {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      ))}
                      {/* Scroll anchor */}
                      <div ref={messagesEndRef} />
                    </div>

                    {/* Scroll to Bottom Button - Positioned above input */}
                    {showScrollButton && messages.length > 0 && (
                      <button
                        onClick={() => scrollToBottom()}
                        className="absolute bottom-20 right-4 w-10 h-10 bg-primary/40 text-white rounded-full shadow-lg hover:shadow-xl hover:bg-primary/60 transition-all duration-200 flex items-center justify-center hover:scale-110 transform z-20"
                        title="Scroll to bottom"
                        aria-label="Scroll messages to bottom"
                      >
                        <Icon name="ArrowDown" size={20} />
                      </button>
                    )}

                    {/* Input */}
                    {selectedConversation.status !== 'closed' && selectedConversation.status !== 'resolved' && (
                      <div className="p-4 border-t border-border space-y-3">
                        {uiError && (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                            {uiError}
                            <button
                              onClick={() => setUiError(null)}
                              className="ml-2 text-red-600 hover:text-red-800 font-semibold"
                            >
                              ×
                            </button>
                          </div>
                        )}
                        <div className="flex gap-2 flex-wrap">
                          <button
                            onClick={() => setShowEscalateModal(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors text-sm font-medium"
                          >
                            <Icon name="AlertCircle" size={16} />
                            Escalate to Ticket
                          </button>
                        </div>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                            placeholder="Type your message..."
                            className="flex-1 px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                            disabled={messagesLoading}
                            aria-label="Message input"
                          />
                          <Button
                            variant="primary"
                            onClick={handleSendMessage}
                            disabled={!newMessage.trim() || messagesLoading}
                            className="flex-shrink-0"
                          >
                            {messagesLoading ? 'Sending...' : 'Send'}
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {/* Show message for resolved/closed conversations */}
                    {(selectedConversation.status === 'closed' || selectedConversation.status === 'resolved') && (
                      <div className="p-4 border-t border-border bg-gray-50 text-center">
                        <div className="flex items-center justify-center gap-2 text-text-muted">
                          <Icon name="Lock" size={16} />
                          <span>This conversation is {selectedConversation.status}. Messages cannot be sent.</span>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex items-center justify-center h-full text-center">
                    <div>
                      <Icon name="MessageCircle" size={48} className="mx-auto text-gray-400 mb-4" />
                      <p className="text-text-muted">Select a conversation to start chatting</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tickets Tab */}
          {activeTab === 'tickets' && (
            <div className="bg-white rounded-lg border border-border overflow-hidden">
              {/* Search and Filters */}
              <div className="p-6 border-b border-border">
                <div className="flex flex-col md:flex-row gap-4 items-end">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-text-muted mb-2">Search Tickets</label>
                    <div className="relative">
                      <Icon name="Search" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search by ticket number, title, customer, or tags..."
                        value={ticketList.searchQuery}
                        onChange={(e) => ticketList.setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                        aria-label="Search tickets"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={ticketList.filter}
                      onChange={(e) => ticketList.setFilter(e.target.value)}
                      className="px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      aria-label="Filter tickets by status"
                    >
                      <option value="all">All Status</option>
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                    <select
                      value={ticketList.sortBy}
                      onChange={(e) => ticketList.setSortBy(e.target.value)}
                      className="px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      aria-label="Sort tickets"
                    >
                      <option value="created">Sort by Created</option>
                      <option value="updated">Sort by Updated</option>
                      <option value="priority">Sort by Priority</option>
                    </select>
                  </div>
                </div>

                {/* Bulk Actions */}
                {ticketList.selectedTickets.size > 0 && (
                  <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                    <p className="text-sm text-text-muted">
                      {ticketList.selectedTickets.size} ticket{ticketList.selectedTickets.size > 1 ? 's' : ''} selected
                    </p>
                    <div className="flex gap-2">
                      <select
                        onChange={(e) => {
                          if (e.target.value) {
                            handleBulkTicketStatusUpdate(e.target.value);
                            e.target.value = '';
                          }
                        }}
                        className="px-3 py-2 border border-border rounded-lg text-sm"
                        defaultValue=""
                        aria-label="Bulk update ticket status"
                      >
                        <option value="">Update status...</option>
                        <option value="open">Mark Open</option>
                        <option value="in_progress">Mark In Progress</option>
                        <option value="resolved">Mark Resolved</option>
                        <option value="closed">Mark Closed</option>
                      </select>
                      <Button
                        variant="secondary"
                        onClick={() => ticketList.setSelectedTickets(new Set())}
                      >
                        Clear Selection
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Tickets Table */}
              <div className="overflow-x-auto">
                {ticketList.loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                  </div>
                ) : ticketList.error ? (
                  <div className="p-8 text-center text-red-600">
                    <p className="text-sm">{ticketList.error}</p>
                  </div>
                ) : filteredTickets.length === 0 ? (
                  <div className="text-center py-12">
                    <Icon name="Ticket" size={48} className="mx-auto text-gray-400 opacity-30 mb-4" />
                    <p className="text-text-muted">No tickets found</p>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-border">
                      <tr>
                        <th className="px-6 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={ticketList.selectedTickets.size === filteredTickets.length && filteredTickets.length > 0}
                            onChange={(e) => {
                              if (e.target.checked) {
                                ticketList.setSelectedTickets(new Set(filteredTickets.map(t => t.id)));
                              } else {
                                ticketList.setSelectedTickets(new Set());
                              }
                            }}
                            aria-label="Select all tickets"
                          />
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                          Ticket #
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                          Customer
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                          Title
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                          Priority
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                          Assigned To
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                          Created
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-text-muted uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-border">
                      {filteredTickets.map((ticket) => (
                        <tr 
                          key={ticket.id}
                          className="hover:bg-gray-50 transition-colors cursor-pointer"
                          onClick={() => {
                            setSelectedTicket(ticket);
                            setShowTicketDetail(true);
                          }}
                        >
                          <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={ticketList.selectedTickets.has(ticket.id)}
                              onChange={(e) => {
                                const newSet = new Set(ticketList.selectedTickets);
                                if (e.target.checked) {
                                  newSet.add(ticket.id);
                                } else {
                                  newSet.delete(ticket.id);
                                }
                                ticketList.setSelectedTickets(newSet);
                              }}
                              aria-label={`Select ticket ${ticket.ticket_number}`}
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="font-mono text-sm text-primary font-medium">
                              {ticket.ticket_number}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              {ticket.user?.profile_picture && (
                                <img
                                  src={ticket.user.profile_picture}
                                  alt={ticket.user.full_name}
                                  className="w-8 h-8 rounded-full object-cover"
                                />
                              )}
                              <div>
                                <p className="text-sm font-medium text-text-primary">
                                  {ticket.user?.full_name}
                                </p>
                                <p className="text-xs text-text-muted">
                                  {ticket.user?.email}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="max-w-xs">
                              <p className="text-sm font-medium text-text-primary truncate">
                                {ticket.title}
                              </p>
                              {ticket.tags && ticket.tags.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {ticket.tags.slice(0, 2).map((tag, idx) => (
                                    <span
                                      key={idx}
                                      className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded"
                                    >
                                      {tag}
                                    </span>
                                  ))}
                                  {ticket.tags.length > 2 && (
                                    <span className="text-xs text-text-muted">
                                      +{ticket.tags.length - 2}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-3 py-1 text-xs font-medium rounded-lg border ${statusColors[ticket.status]}`}>
                              {ticket.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-3 py-1 text-xs font-medium rounded-lg ${priorityColors[ticket.priority]}`}>
                              {ticket.priority}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {ticket.assigned_admin ? (
                              <div className="flex items-center gap-2">
                                {ticket.assigned_admin.profile_picture && (
                                  <img
                                    src={ticket.assigned_admin.profile_picture}
                                    alt={ticket.assigned_admin.full_name}
                                    className="w-6 h-6 rounded-full object-cover"
                                  />
                                )}
                                <span className="text-sm text-text-primary">
                                  {ticket.assigned_admin.full_name}
                                </span>
                              </div>
                            ) : (
                              <span className="text-xs text-text-muted italic">Unassigned</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-text-muted">
                            {new Date(ticket.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTicket(ticket);
                                setShowTicketDetail(true);
                              }}
                            >
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Ticket Detail Modal */}
      <TicketDetail
        ticket={selectedTicket}
        isOpen={showTicketDetail}
        onClose={() => {
          setShowTicketDetail(false);
          setSelectedTicket(null);
        }}
        onUpdate={handleTicketUpdate}
      />

      {/* Escalate to Ticket Modal */}
      <EscalateToTicketModal
        conversationId={selectedConversation?.id}
        isOpen={showEscalateModal}
        onClose={() => setShowEscalateModal(false)}
        onSuccess={(ticket) => {
          // Refresh tickets list and show confirmation
          ticketList.loadTickets();
          ticketList.loadAnalytics();
          setShowEscalateModal(false);
          // Show success notification (consider using a proper toast/notification system)
          alert(`✅ Ticket ${ticket.ticket_number} created successfully!\n\nThis conversation has been escalated to a support ticket.`);
        }}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirmModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <Icon name="AlertTriangle" size={24} className="text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-text-primary">
                    {deleteConfirmModal.isBulk ? 'Delete Multiple Conversations?' : 'Delete Conversation?'}
                  </h3>
                  <p className="text-sm text-text-muted mt-1">This action cannot be undone</p>
                </div>
              </div>

              {deleteConfirmModal.isBulk && deleteConfirmModal.conversations ? (
                <div className="bg-gray-50 rounded p-3 mb-6">
                  <p className="text-sm font-medium text-text-primary">
                    {deleteConfirmModal.conversations.length} archived conversation(s) will be permanently deleted
                  </p>
                  <ul className="mt-2 text-xs text-text-muted space-y-1 max-h-32 overflow-y-auto">
                    {deleteConfirmModal.conversations.slice(0, 5).map(conv => (
                      <li key={conv.id} className="truncate">• {conv.user?.full_name} - {conv.subject}</li>
                    ))}
                    {deleteConfirmModal.conversations.length > 5 && (
                      <li className="italic">...and {deleteConfirmModal.conversations.length - 5} more</li>
                    )}
                  </ul>
                </div>
              ) : deleteConfirmModal.conversation && (
                <div className="bg-gray-50 rounded p-3 mb-6">
                  <p className="text-sm font-medium text-text-primary truncate">
                    {deleteConfirmModal.conversation.user?.full_name}
                  </p>
                  <p className="text-xs text-text-muted truncate mt-1">
                    {deleteConfirmModal.conversation.subject}
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirmModal({ show: false, conversation: null, isBulk: false, conversations: [] })}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2 text-sm font-medium text-text-primary bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Icon name="Trash2" size={16} />
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSupportDashboard;
